import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Camera, Zap, CheckCircle2, Smartphone, ArrowLeftRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Typeahead, type TypeaheadOption } from '@/components/ui/typeahead';
import { LoadingSpinner } from '@/components/ui/loading';
import { FIELD_MAX_LENGTHS } from '@/lib/validation-constants';
import type { Building, Unit } from '@/types/api';
import { QrScanner } from '@/features/setup/components/qr-scanner';
import {
  lookupDeviceForScan,
  commitDeviceSetup,
  SetupDeviceError,
  type ScanLookupResult,
} from '@/features/setup/utils/setup-device-without-transmission';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { PhonePairingPanel } from '@/features/pairing/components/phone-pairing-panel';
import {
  computeNextAvailableUnit,
  nextUnitAfter as nextUnitAfterUtil,
} from '@/features/setup/utils/unit-numbering';

interface SetupModePanelProps {
  building: Building | null;
  buildings: Building[];
  existingUnits: Unit[];
  onBuildingSelect: (id: string) => void;
  /** Create a building under the current property and return its id (or null on failure). */
  onBuildingCreate: (name: string) => Promise<string | null>;
  onExit: () => void;
  onScanned: (info: { deviceId: string; serialNumber: string; unitNumber: string }) => void;
}

// Lowest positive integer not in use by any existing unit. Seeds the form
// when the installer enters Setup Mode (and after a non-numeric submit).
// Non-numeric labels ("Penthouse", "1A") are ignored. See
// `@/features/setup/utils/unit-numbering` for full semantics.
function computeNextBuildingUnit(units: Unit[]): string {
  return computeNextAvailableUnit(units.map((u) => ({ number: u.unitNumber })));
}

function nextUnitAfter(submitted: string, units: Unit[]): string {
  return nextUnitAfterUtil(submitted, units.map((u) => ({ number: u.unitNumber })));
}

export function SetupModePanel({ building, buildings, existingUnits, onBuildingSelect, onBuildingCreate, onExit, onScanned }: SetupModePanelProps) {
  const navigate = useNavigate();
  const [buildingPickerInput, setBuildingPickerInput] = useState('');
  const [buildingPickerCreating, setBuildingPickerCreating] = useState(false);
  const [buildingPickerError, setBuildingPickerError] = useState<string | null>(null);
  const buildingOptions = useMemo<TypeaheadOption[]>(() => {
    const trimmed = buildingPickerInput.trim().toLowerCase();
    return buildings
      .filter((b) => !trimmed || b.name.toLowerCase().includes(trimmed))
      .map((b) => ({ value: b.id, label: b.name }));
  }, [buildings, buildingPickerInput]);
  const buildingNameTaken = useMemo(() => {
    const trimmed = buildingPickerInput.trim().toLowerCase();
    return !!trimmed && buildings.some((b) => b.name.toLowerCase() === trimmed);
  }, [buildings, buildingPickerInput]);

  async function handleBuildingPickerCreate(name: string) {
    setBuildingPickerCreating(true);
    setBuildingPickerError(null);
    try {
      const newId = await onBuildingCreate(name.trim());
      if (newId) {
        setBuildingPickerInput('');
        onBuildingSelect(newId);
      } else {
        setBuildingPickerError('Could not create building. Please try again.');
      }
    } finally {
      setBuildingPickerCreating(false);
    }
  }

  const [serialNumber, setSerialNumber] = useState('');
  // `unitOverride` is the installer's typed/submit-derived value. When null,
  // the field shows the seeded default (building's max numeric unit + 1).
  // This lets the default reactively update if existingUnits arrives after
  // mount or after a scan adds a new unit.
  const [unitOverride, setUnitOverride] = useState<string | null>(null);
  // Meter type is no longer collected here. The decoder lambda classifies
  // every uplink (FPort + DevEUI range) and the API auto-corrects the
  // device's stored type on join/first-uplink. SALT pulse-encoder calibration
  // is collected from a CTA on the device detail page once the meter is
  // installed and reachable.
  const seededUnit = useMemo(() => computeNextBuildingUnit(existingUnits), [existingUnits]);
  const unitNumber = unitOverride ?? seededUnit;
  const [showScanner, setShowScanner] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [scanCount, setScanCount] = useState(0);
  const [pendingConfirm, setPendingConfirm] = useState<
    | (Extract<ScanLookupResult, { kind: 'needs-confirm' }> & { targetUnit: string })
    | null
  >(null);
  const [confirmInFlight, setConfirmInFlight] = useState(false);
  const [showPairing, setShowPairing] = useState(false);
  const [dismissedPairHint, setDismissedPairHint] = useState(
    () => localStorage.getItem('norgas_setup_pair_hint_dismissed') === '1',
  );
  const serialInputRef = useRef<HTMLInputElement>(null);
  const unitInputRef = useRef<HTMLInputElement>(null);
  // Auto-dismiss timer for the success banner. Cleared on each new
  // success so back-to-back scans restart the 2.5s window cleanly.
  const successDismissRef = useRef<number | null>(null);

  useEffect(() => () => {
    if (successDismissRef.current) window.clearTimeout(successDismissRef.current);
  }, []);

  // Pre-building-selection state: show a building picker first. The session
  // locks to whichever building the installer commits to.
  if (!building) {
    return (
      <Card variant="elevated" className="p-4 space-y-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-xs font-semibold text-primary-700 dark:text-primary-400 uppercase tracking-wide">
              Setup Mode
            </p>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Pick a building
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Scans lock to this building for the session.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={onExit} aria-label="Exit Setup Mode">
            <X className="w-4 h-4" />
            <span>Exit Setup Mode</span>
          </Button>
        </div>
        <Typeahead
          label="Building"
          required
          placeholder={buildings.length === 0 ? 'Type a name to add the first building…' : 'Search or add a building…'}
          inputValue={buildingPickerInput}
          onInputChange={setBuildingPickerInput}
          onSelect={(opt) => onBuildingSelect(opt.value)}
          onCreateNew={handleBuildingPickerCreate}
          options={buildingOptions}
          isLoading={buildingPickerCreating}
          error={buildingPickerError ?? undefined}
          maxLength={FIELD_MAX_LENGTHS.BUILDING_NAME}
          allowCreate={!buildingNameTaken}
          createLabel="Add Building"
        />
      </Card>
    );
  }

  async function finalizeCommit(lookup: ScanLookupResult, targetUnit: string) {
    if (!building) return;
    const result = await commitDeviceSetup({
      lookup,
      buildingId: building.id,
      unitNumber: targetUnit,
    });
    setScanCount((n) => n + 1);
    const typeBadge = result.device.deviceType === 'SALTTransmitter' ? ' (SALT)' : '';
    setSuccessMessage(
      result.reassigned
        ? `Reassigned ${result.device.serialNumber}${typeBadge} to Unit ${result.unit.unitNumber}.`
        : `Paired ${result.device.serialNumber}${typeBadge} to Unit ${result.unit.unitNumber}.`
    );
    // Mirror the camera flow: auto-dismiss the visible toast after 2.5s
    // so the form area isn't permanently occupied by a stale banner.
    if (successDismissRef.current) window.clearTimeout(successDismissRef.current);
    successDismissRef.current = window.setTimeout(() => {
      setSuccessMessage(null);
    }, 2500);
    setSerialNumber('');
    setUnitOverride(nextUnitAfter(targetUnit, existingUnits));
    onScanned({
      deviceId: result.device.id,
      serialNumber: result.device.serialNumber,
      unitNumber: result.unit.unitNumber,
    });
    // Return focus to the serial field so a hand-held scanner's next
    // scan lands in the right input.
    serialInputRef.current?.focus();
  }

  async function runSetup(serial: string, unit: string) {
    if (!building) return;
    if (!serial || !unit) {
      setError('Enter both a serial number and a unit number.');
      return;
    }
    setSubmitting(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const lookup = await lookupDeviceForScan({
        buildingId: building.id,
        serialNumber: serial,
        unitNumber: unit,
      });
      if (lookup.kind === 'needs-confirm') {
        setPendingConfirm({ ...lookup, targetUnit: unit });
        return;
      }
      await finalizeCommit(lookup, unit);
    } catch (err) {
      if (err instanceof SetupDeviceError) {
        setError(err.message);
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  // Re-run lookup before committing so a concurrent change (another
  // installer reassigning the same serial) is detected and surfaced as
  // "scan again" instead of clobbering.
  async function handleConfirmReassign() {
    if (!building || !pendingConfirm) return;
    setConfirmInFlight(true);
    setError(null);
    try {
      const fresh = await lookupDeviceForScan({
        buildingId: building.id,
        serialNumber: pendingConfirm.serial,
        unitNumber: pendingConfirm.targetUnit,
      });
      if (fresh.kind !== 'needs-confirm' || fresh.device.unitId !== pendingConfirm.device.unitId) {
        setError('Device assignment changed since you scanned. Please scan again.');
        setPendingConfirm(null);
        return;
      }
      await finalizeCommit(fresh, pendingConfirm.targetUnit);
      setPendingConfirm(null);
    } catch (err) {
      if (err instanceof SetupDeviceError) {
        setError(err.message);
      } else {
        setError('Something went wrong. Please try again.');
      }
      setPendingConfirm(null);
    } finally {
      setConfirmInFlight(false);
    }
  }

  function handleCancelReassign() {
    setPendingConfirm(null);
  }

  function handleSubmit() {
    void runSetup(serialNumber.trim(), unitNumber.trim());
  }

  // "Change" affordance: returns to the picker pre-screen for properties
  // with multiple buildings. Clears the in-progress form so a fresh session
  // starts in the new building, but preserves the session scan counter and
  // the pair-phone hint dismissal so the installer's running progress and
  // settings persist across the swap.
  function handleChangeBuilding() {
    setSerialNumber('');
    setUnitOverride(null);
    setError(null);
    setSuccessMessage(null);
    setShowScanner(false);
    onBuildingSelect('');
  }

  // Paste the serial (e.g. from a QR app or a scanner configured to send
  // Ctrl+V) and — if we already have a unit number queued up — run Set Up
  // immediately. Keeps the one-handed scan flow working without hitting
  // Enter or tapping a button.
  function handleSerialPaste(e: React.ClipboardEvent<HTMLInputElement>) {
    const pasted = e.clipboardData.getData('text').replace(/^SN\s*/i, '').trim().toUpperCase();
    if (!pasted) return;
    e.preventDefault();
    setSerialNumber(pasted);
    setError(null);
    const unit = unitNumber.trim();
    if (unit) {
      void runSetup(pasted, unit);
    } else {
      unitInputRef.current?.focus();
    }
  }

  return (
    <Card variant="elevated" className="p-4 space-y-4">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold text-primary-700 dark:text-primary-400 uppercase tracking-wide">
            Setup Mode
          </p>
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">
              {building.name}
            </h2>
            {buildings.length > 1 && (
              <button
                type="button"
                onClick={handleChangeBuilding}
                className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 hover:underline focus:outline-none focus:ring-2 focus:ring-primary-500/40 rounded px-1.5 -mx-0.5 py-0.5 transition-colors"
                aria-label="Change building"
              >
                <ArrowLeftRight className="w-3 h-3" />
                Change
              </button>
            )}
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 hidden sm:block">
            Scan meters into this building. Rows stay red until the magnet transmission lands.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Pair Phone is desktop-only — on mobile the installer is
              already on a phone, so generating a QR for "another phone
              to scan" is nonsensical. */}
          <Button
            variant="outline"
            size="sm"
            className="hidden sm:flex"
            onClick={() => setShowPairing(true)}
            aria-label="Pair a phone"
          >
            <Smartphone className="w-4 h-4" />
            Pair phone
          </Button>
          <Button variant="outline" size="sm" onClick={onExit} aria-label="Exit Setup Mode">
            <X className="w-4 h-4" />
            <span>Exit Setup Mode</span>
          </Button>
        </div>
      </div>

      {!dismissedPairHint && (
        // Pair-phone hint is desktop-only — same rationale as the Pair
        // Phone button itself: mobile users are already on a phone.
        <div className="hidden sm:flex items-start gap-2 text-xs rounded-lg border border-primary-200 dark:border-primary-700 bg-primary-50 dark:bg-primary-900/20 px-3 py-2 text-gray-700 dark:text-gray-300">
          <Smartphone className="w-4 h-4 text-primary-600 dark:text-primary-400 flex-shrink-0 mt-0.5" />
          <p className="flex-1 leading-relaxed">
            Using a phone camera or hand-scanner is faster.{' '}
            <button
              type="button"
              className="underline font-medium text-primary-700 dark:text-primary-300"
              onClick={() => setShowPairing(true)}
            >
              Pair your phone
            </button>
            .
          </p>
          <button
            type="button"
            aria-label="Dismiss"
            onClick={() => {
              localStorage.setItem('norgas_setup_pair_hint_dismissed', '1');
              setDismissedPairHint(true);
            }}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* On mobile, "Scan QR Code" routes the installer into the dedicated
          full-screen continuous-scan view (same UX as paired-phone scanning)
          rather than opening a one-shot modal over the form. Primary blue
          per Tanner's Apr 30 feedback — the outline variant blended into the
          form below and installers were skipping straight to typing the
          serial. */}
      {!showScanner && (
        <Button
          type="button"
          variant="primary"
          size="lg"
          className="w-full md:hidden"
          onClick={() => navigate(
            `/pair/scanner?propertyId=${building?.propertyId ?? ''}&buildingId=${building?.id ?? ''}&from=setup-mode`
          )}
        >
          <Camera className="w-5 h-5" />
          Scan QR Code
        </Button>
      )}

      <div className="grid gap-3 md:grid-cols-[1fr_1fr_auto] md:items-end">
        <Input
          ref={serialInputRef}
          label="Serial Number"
          placeholder="e.g., 0123456789ABCDEF"
          value={serialNumber}
          onChange={(e) => {
            setSerialNumber(e.target.value.toUpperCase());
            setError(null);
          }}
          onPaste={handleSerialPaste}
          // Hand-held barcode/QR scanners act as keyboards and usually
          // emit Enter at the end of the scan. Jump focus to the unit
          // field so the installer's next action (type unit, Enter to
          // submit) is one-handed.
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              if (unitNumber.trim() && serialNumber.trim()) {
                handleSubmit();
              } else {
                unitInputRef.current?.focus();
              }
            }
          }}
          maxLength={FIELD_MAX_LENGTHS.DEV_EUI}
          autoFocus
          autoCapitalize="characters"
          autoCorrect="off"
          spellCheck={false}
        />
        <Input
          ref={unitInputRef}
          label="Unit Number"
          placeholder="e.g., 101"
          value={unitNumber}
          onChange={(e) => {
            setUnitOverride(e.target.value);
            setError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && serialNumber.trim() && unitNumber.trim()) {
              e.preventDefault();
              handleSubmit();
            }
          }}
          maxLength={FIELD_MAX_LENGTHS.UNIT_NUMBER}
          autoCorrect="off"
          spellCheck={false}
        />
        <Button
          onClick={handleSubmit}
          disabled={submitting || !serialNumber.trim() || !unitNumber.trim()}
          className="md:w-28 py-3 md:py-2"
        >
          {submitting ? <LoadingSpinner size="sm" /> : (<><Zap className="w-4 h-4" />Set Up</>)}
        </Button>
      </div>

      {/* Desktop: smaller Scan QR button inline with the session counter.
          On mobile the big scan button above replaces this. */}
      <div className="hidden md:flex gap-2 items-center">
        {!showScanner && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setShowScanner(true)}
          >
            <Camera className="w-3.5 h-3.5" />
            Scan QR
          </Button>
        )}
        <p className="text-sm text-gray-500 dark:text-gray-400 ml-auto">
          Scanned this session: <span className="font-medium text-gray-900 dark:text-gray-100">{scanCount}</span>
        </p>
      </div>
      <p className="md:hidden text-sm text-gray-500 dark:text-gray-400 text-center">
        Scanned this session: <span className="font-medium text-gray-900 dark:text-gray-100">{scanCount}</span>
      </p>

      {showScanner && (
        <QrScanner
          onScan={(value) => {
            setShowScanner(false);
            setSerialNumber(value.replace(/^SN\s*/i, '').toUpperCase());
          }}
          onClose={() => setShowScanner(false)}
        />
      )}

      {/* Screen-reader assertive error stream — error banner below isn't
          an aria-live region (it's flat). The success banner now has its
          own role="status" wrapper so we don't need a sr-only mirror. */}
      <p role="alert" aria-live="assertive" aria-atomic="true" className="sr-only">
        {error}
      </p>
      {error && (
        <div className="text-sm text-error-700 dark:text-error-400 bg-error-50 dark:bg-error-500/10 border border-error-200 dark:border-error-500/30 rounded-lg px-3 py-2">
          {error}
        </div>
      )}
      {successMessage && !error && (
        <div
          role="status"
          aria-live="polite"
          aria-atomic="true"
          className="norgas-flash-in flex items-center gap-2 text-base font-medium text-success-700 dark:text-success-400 bg-success-50 dark:bg-success-500/10 border border-success-200 dark:border-success-500/30 rounded-lg px-4 py-3"
        >
          <CheckCircle2 className="w-6 h-6 flex-shrink-0" aria-hidden="true" />
          {successMessage}
        </div>
      )}

      <PhonePairingPanel
        isOpen={showPairing}
        onClose={() => setShowPairing(false)}
        propertyId={building.propertyId}
        buildingId={building.id}
        propertyName={building.propertyName}
        buildingName={building.name}
      />

      {/* Reassignment confirm — only fires when the scanned serial is
          currently on a different unit. No auto-cancel; the modal waits
          for an explicit answer to keep the flow accessible to screen-
          reader users. Cancel = no commit; Esc and click-outside cancel. */}
      <ConfirmDialog
        isOpen={pendingConfirm !== null}
        title="Reassign meter to a different unit?"
        description={
          pendingConfirm ? (
            <div className="space-y-2">
              <p>
                Serial <span className="font-mono font-semibold">{pendingConfirm.serial}</span> is currently assigned to{' '}
                <strong>
                  {pendingConfirm.sourceBuildingName ? `${pendingConfirm.sourceBuildingName} · ` : ''}
                  {pendingConfirm.sourceUnitNumber ?? '—'}
                </strong>.
              </p>
              <p>
                Reassign to <strong>{pendingConfirm.targetUnit}</strong>?
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                This usually only happens when a meter is being moved, or a different QR was scanned by mistake.
              </p>
            </div>
          ) : null
        }
        confirmLabel="Reassign"
        cancelLabel="Cancel"
        variant="primary"
        onConfirm={handleConfirmReassign}
        onCancel={handleCancelReassign}
        isLoading={confirmInFlight}
      />
    </Card>
  );
}
